import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { productEffectivePrice } from '../productPricing';
import { customizationUnitPriceDelta } from '../jewelry/jewelryCustomization';
import { cartApiPost, cartLinePayload, serverRowsToCartLines } from './cartApi';
import {
  cartItemCount,
  cartLineKey,
  cartStorageKey,
  cartSubtotal,
  clearCartFromStorage,
  isOutOfStock,
  lineMaxQty,
  normalizeStockQty,
  readCartFromStorage,
  resolveCartStorageScope,
  stockLimitMessage,
  writeCartToStorage,
} from './cartUtils';
import { trackAddToCart } from '../analytics';
import { logFunnelEvent, syncGuestCart } from '../storefront/storefrontEvents';

const CartContext = createContext(null);
const CART_SAVE_DEBOUNCE_MS = 700;

function capLineQty(line, qty) {
  const max = lineMaxQty(line.stockQty);
  if (max <= 0) return 0;
  return Math.min(max, Math.max(1, Math.floor(qty)));
}

export function CartProvider({ children }) {
  const location = useLocation();
  const { ready, isAuthed, token, username, isStaff } = useAuth();
  const cartScope = useMemo(
    () => resolveCartStorageScope({ ready, isStaff, isAuthed, username }),
    [ready, isStaff, isAuthed, username]
  );
  const cartEnabled = cartScope != null;

  const [lines, setLines] = useState([]);
  const [toast, setToast] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [serverEnabled, setServerEnabled] = useState(true);
  const [cartLoading, setCartLoading] = useState(false);
  const syncedTokenRef = useRef('');
  const skipServerSaveRef = useRef(false);
  const saveTimerRef = useRef(null);
  const guestSaveTimerRef = useRef(null);
  const cartScopeRef = useRef(cartScope);

  const loadFromServer = useCallback(
    async (mergeGuestLocal = false) => {
      if (!isAuthed || isStaff) {
        setLines([]);
        return;
      }
      setCartLoading(true);
      skipServerSaveRef.current = true;
      try {
        const guestLocal = mergeGuestLocal ? readCartFromStorage('guest') : [];
        if (mergeGuestLocal && guestLocal.length > 0) {
          const syncRes = await cartApiPost('sync_cart', {
            items: guestLocal.map(cartLinePayload),
          });
          if (!syncRes.ok) {
            setLines(guestLocal);
            return;
          }
          setServerEnabled(syncRes.data?.enabled !== false);
          const merged = serverRowsToCartLines(syncRes.data?.items);
          setLines(merged);
          clearCartFromStorage('guest');
          if (cartScope) writeCartToStorage(merged, cartScope);
          return;
        }
        const res = await cartApiPost('list_cart');
        if (!res.ok) {
          setLines([]);
          return;
        }
        setServerEnabled(res.data?.enabled !== false);
        const fromServer = serverRowsToCartLines(res.data?.items);
        setLines(fromServer);
        if (cartScope) writeCartToStorage(fromServer, cartScope);
      } finally {
        skipServerSaveRef.current = false;
        setCartLoading(false);
      }
    },
    [isAuthed, isStaff, cartScope]
  );

  useEffect(() => {
    if (!ready) return undefined;

    const prevScope = cartScopeRef.current;
    cartScopeRef.current = cartScope;

    if (cartScope === null) {
      setLines([]);
      setDrawerOpen(false);
      syncedTokenRef.current = '';
      return undefined;
    }

    if (!isAuthed) {
      syncedTokenRef.current = '';
      setLines(readCartFromStorage(cartScope));
      return undefined;
    }

    const tok = String(token || '').trim();
    if (!tok) return undefined;

    const scopeChanged = prevScope !== cartScope;
    const tokenChanged = syncedTokenRef.current !== tok;
    if (tokenChanged || scopeChanged) {
      syncedTokenRef.current = tok;
      void loadFromServer(scopeChanged && prevScope === 'guest');
    }

    return undefined;
  }, [ready, isAuthed, isStaff, token, cartScope, loadFromServer]);

  useEffect(() => {
    if (!ready || !isAuthed || isStaff || !serverEnabled || skipServerSaveRef.current) {
      return undefined;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void cartApiPost('save_cart', { items: lines.map(cartLinePayload) });
    }, CART_SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [lines, ready, isAuthed, isStaff, serverEnabled]);

  useEffect(() => {
    if (!cartScope) return undefined;
    writeCartToStorage(lines, cartScope);
    return undefined;
  }, [lines, cartScope]);

  useEffect(() => {
    if (guestSaveTimerRef.current) {
      clearTimeout(guestSaveTimerRef.current);
      guestSaveTimerRef.current = null;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!ready || isAuthed || cartScope !== 'guest') return undefined;
    if (guestSaveTimerRef.current) clearTimeout(guestSaveTimerRef.current);
    guestSaveTimerRef.current = setTimeout(() => {
      try {
        syncGuestCart(lines);
      } catch {
        /* analytics must never break cart or navigation */
      }
    }, CART_SAVE_DEBOUNCE_MS);
    return () => {
      if (guestSaveTimerRef.current) clearTimeout(guestSaveTimerRef.current);
    };
  }, [lines, ready, isAuthed, cartScope]);

  useEffect(() => {
    if (!cartScope) return undefined;
    const storageKey = cartStorageKey(cartScope);
    if (!storageKey) return undefined;
    const onStorage = (e) => {
      if (e.key !== storageKey || skipServerSaveRef.current) return;
      setLines(readCartFromStorage(cartScope));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [cartScope]);

  const addItem = useCallback(
    (product, categorySlug, qty = 1, customization = '') => {
      if (!cartEnabled) {
        return { ok: false, error: 'Admin accounts do not use the customer bag. Shop in a private window as a customer.' };
      }
      const custom = String(customization || '').trim();
      const price = productEffectivePrice(product) + customizationUnitPriceDelta(custom, product);
      if (price <= 0) return { ok: false, error: 'This item needs a custom quote. Contact us on WhatsApp.' };
      const productId = String(product?.id || '').trim();
      const cat = String(categorySlug || '').trim();
      if (!productId || !cat) return { ok: false, error: 'Invalid product.' };

      const stockQty = normalizeStockQty(product?.stock_qty);
      const name = String(product?.name || productId);
      if (isOutOfStock(stockQty)) {
        return { ok: false, error: stockLimitMessage(name, 0) };
      }
      const max = lineMaxQty(stockQty);
      const amount = Math.max(1, Math.min(max, Number(qty) || 1));
      const key = cartLineKey(cat, productId, custom);
      const image = product?.image || (Array.isArray(product?.images) ? product.images[0] : '') || '';

      let limitError = '';
      setLines((prev) => {
        const idx = prev.findIndex(
          (l) => cartLineKey(l.categorySlug, l.productId, l.customization) === key
        );
        if (idx >= 0) {
          const wouldBe = prev[idx].qty + amount;
          if (wouldBe > max) {
            limitError = stockLimitMessage(name, max);
            return prev;
          }
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            name,
            price,
            image: String(image || next[idx].image || ''),
            stockQty,
            qty: wouldBe,
          };
          return next;
        }
        return [
          ...prev,
          {
            categorySlug: cat,
            productId,
            name,
            price,
            image: String(image || ''),
            stockQty,
            customization: custom,
            qty: amount,
          },
        ];
      });

      if (limitError) return { ok: false, error: limitError };

      setToast({ productName: name });
      setDrawerOpen(true);
      trackAddToCart({ product, categorySlug: cat, qty: amount, price });
      logFunnelEvent({
        eventType: 'add_to_cart',
        categorySlug: cat,
        productId,
        productName: name,
        quantity: amount,
        valueAmount: price * amount,
        authToken: token || null,
      });
      return { ok: true };
    },
    [cartEnabled, token]
  );

  const openDrawer = useCallback(() => {
    if (!cartEnabled) return;
    setDrawerOpen(true);
  }, [cartEnabled]);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => {
    if (!cartEnabled) return;
    setDrawerOpen((v) => !v);
  }, [cartEnabled]);

  const dismissToast = useCallback(() => setToast(null), []);

  const setQty = useCallback((categorySlug, productId, qty, customization = '') => {
    const key = cartLineKey(categorySlug, productId, customization);
    const n = Number(qty);
    if (!Number.isFinite(n) || n < 1) {
      setLines((prev) =>
        prev.filter((l) => cartLineKey(l.categorySlug, l.productId, l.customization) !== key)
      );
      return;
    }
    setLines((prev) =>
      prev.map((l) => {
        if (cartLineKey(l.categorySlug, l.productId, l.customization) !== key) return l;
        return { ...l, qty: capLineQty(l, n) };
      })
    );
  }, []);

  const removeItem = useCallback(
    (categorySlug, productId, customization = '') => {
      const key = cartLineKey(categorySlug, productId, customization);
      setLines((prev) => {
        const line = prev.find((l) => cartLineKey(l.categorySlug, l.productId, l.customization) === key);
        if (line) {
          logFunnelEvent({
            eventType: 'remove_from_cart',
            categorySlug: line.categorySlug,
            productId: line.productId,
            productName: line.name,
            quantity: line.qty,
            valueAmount: (line.price || 0) * (line.qty || 1),
            authToken: token || null,
          });
        }
        return prev.filter((l) => cartLineKey(l.categorySlug, l.productId, l.customization) !== key);
      });
    },
    [token]
  );

  const clearCart = useCallback(() => {
    setLines([]);
    if (isAuthed && serverEnabled && !isStaff) {
      void cartApiPost('save_cart', { items: [] });
    }
    if (cartScope) clearCartFromStorage(cartScope);
  }, [isAuthed, isStaff, serverEnabled, cartScope]);

  const reconcileStock = useCallback((stockByKey) => {
    if (!stockByKey || stockByKey.size === 0) return { removed: [], adjusted: [] };

    const removed = [];
    const adjusted = [];
    let changed = false;

    setLines((prev) => {
      const next = prev
        .map((line) => {
          const baseKey = cartLineKey(line.categorySlug, line.productId);
          if (!stockByKey.has(baseKey)) return line;

          const stockQty = stockByKey.get(baseKey);
          const max = lineMaxQty(stockQty);
          if (max <= 0) {
            removed.push(line.name);
            changed = true;
            return null;
          }
          const capped = Math.min(line.qty, max);
          if (capped < line.qty) adjusted.push(line.name);
          if (capped !== line.qty || line.stockQty !== stockQty) changed = true;
          return { ...line, stockQty, qty: capped };
        })
        .filter(Boolean);

      return changed ? next : prev;
    });

    return { removed, adjusted };
  }, []);

  const value = useMemo(
    () => ({
      lines,
      itemCount: cartEnabled ? cartItemCount(lines) : 0,
      subtotal: cartEnabled ? cartSubtotal(lines) : 0,
      cartEnabled,
      addItem,
      setQty,
      removeItem,
      clearCart,
      reconcileStock,
      toast,
      dismissToast,
      drawerOpen: cartEnabled && drawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      cartLoading,
      serverCartEnabled: serverEnabled,
      reloadCart: () => loadFromServer(false),
    }),
    [
      lines,
      cartEnabled,
      toast,
      addItem,
      setQty,
      removeItem,
      clearCart,
      reconcileStock,
      dismissToast,
      drawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      cartLoading,
      serverEnabled,
      loadFromServer,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

